import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../db'; // Dexie IndexedDB config
import axios from 'axios';
import AppURL from '../api/AppURL';

const OfflineCRUD = () => {
  const [form, setForm] = useState({ name: '', quantity: '', id: null });
  const [allEntries, setAllEntries] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const token = localStorage.getItem('auth_token');

  // ✅ Load both local and server data
  const loadCombinedData = useCallback(async () => {
    const local = await db.entries.toArray();
    let remote = [];

    try {
      const res = await axios.get(AppURL.AllData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      remote = res.data.map(item => ({
        ...item,
        status: 'Synced',
        from: 'server'
      }));
    } catch (err) {
      console.log('❌ Failed to load Laravel data');
    }

    // 🔁 Final merged entries:
    const merged = [];

    // ✅ Step 1: Add all remote entries first
    for (let r of remote) {
      const localMatch = local.find(l => l.id === r.id);

      if (localMatch) {
        // যদি লোকাল আইটেম থাকে এবং সেটা Synced না হয়, তবে লোকালটাই রাখো
        if (localMatch.status !== 'Synced') {
          merged.push({ ...localMatch, from: 'local' });
        } else {
          merged.push(r); // synced হলে রিমোটটাই রাখো
        }
      } else {
        merged.push(r);
      }
    }

    // ✅ Step 2: Add local-only entries (e.g. new added ones)
    for (let l of local) {
      if (!remote.find(r => r.id === l.id)) {
        merged.push({ ...l, from: 'local' });
      }
    }

    setAllEntries(merged);
  }, [token]);





  // ✅ Sync data to server
  const syncData = useCallback(async () => {
    const pending = await db.entries.filter(e => e.status !== 'Synced').toArray();
    if (!pending.length) return;

    try {
      await axios.post(AppURL.SyncData, { data: pending }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      for (let entry of pending) {
        if (entry.status === 'Pending Delete') {
          await db.entries.delete(entry.id);
        } else {
          await db.entries.update(entry.id, { status: 'Synced' });
        }
      }

      await loadCombinedData();
    } catch (error) {
      console.log('❌ Sync failed:', error);
    }
  }, [token, loadCombinedData]);

  // ✅ Network state listeners
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncData();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    loadCombinedData();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncData, loadCombinedData]);

  // ✅ Handle input change
  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ✅ Handle submit (add or update)
  const handleSubmit = async e => {
    e.preventDefault();
    const isUpdate = form.id !== null;
    const entry = {
      id: form.id || Date.now(), // Client-generated ID
      name: form.name,
      quantity: parseInt(form.quantity),
      status: isUpdate ? 'Pending Update' : 'Pending',
    };

    await db.entries.put(entry);
    setForm({ name: '', quantity: '', id: null });

    if (navigator.onLine) await syncData();
    await loadCombinedData();
  };

  // ✅ Edit button handler
  const handleEdit = (entry) => {
    setForm({ name: entry.name, quantity: entry.quantity, id: entry.id });
  };

  // ✅ Delete button handler
  const handleDelete = async (entry) => {
    const toDelete = { ...entry, status: 'Pending Delete' };
    await db.entries.put(toDelete);
    if (navigator.onLine) await syncData();
    await loadCombinedData();
  };

  return (
    <div style={{ padding: 20 }}>
      <h2>🌐 Offline-First CRUD</h2>
      <form onSubmit={handleSubmit}>
        <input name="name" value={form.name} onChange={handleChange} placeholder="Name" required />
        <input name="quantity" value={form.quantity} onChange={handleChange} placeholder="Quantity" type="number" required />
        <button type="submit">{form.id ? 'Update' : 'Add'}</button>
      </form>

      <p>Status: {isOnline ? '🟢 Online' : '🔴 Offline'}</p>

      <h3>📋 All Entries</h3>
      <table border="1" width="100%" cellPadding={5}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Quantity</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {allEntries.map(e => (
            <tr key={e.id}>
              <td>{e.name}</td>
              <td>{e.quantity}</td>
              <td>{e.status === 'Synced' ? '✅ Synced' : '⏳ ' + e.status}</td>
              <td>
                {e.status !== 'Pending Delete' && (
                  <>
                    <button onClick={() => handleEdit(e)}>✏️</button>
                    <button onClick={() => handleDelete(e)}>🗑️</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default OfflineCRUD;
